'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import Download2LineIcon from 'remixicon-react/Download2LineIcon';
import { Sparkles, Globe, ExternalLink } from 'lucide-react';
import { useAppSidebarStore } from '@/stores/layout/appSidebarStore';
import { TetherCoin3D, BazarSvg, MyKetSvg } from '@/components/Icons';
import { safeHtmlLinks } from '@/utils/links/safeHref';

export default function AppSidebarDrawer() {
    const { isOpen, closeSidebar, data, isLoading } = useAppSidebarStore();
    const [mounted, setMounted] = useState(false);
    const [isAnimated, setIsAnimated] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    // Detect iOS client-side (safe from SSR hydration mismatch)
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const ua =
                navigator.userAgent ||
                navigator.vendor ||
                (window as unknown as { opera?: string }).opera ||
                '';
            const isAppleDevice =
                /iPad|iPhone|iPod/.test(ua) ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            setIsIOS(isAppleDevice);
        }
    }, []);

    // Synchronize initial mount & paint with entrance/exit CSS transitions (300ms)
    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isOpen) {
            setMounted(true);
            // Double rAF / microtask ensures browser paints initial closed state before triggering transition
            const r1 = requestAnimationFrame(() => {
                timer = setTimeout(() => setIsAnimated(true), 25);
            });
            return () => {
                cancelAnimationFrame(r1);
                clearTimeout(timer);
            };
        } else {
            setIsAnimated(false);
            timer = setTimeout(() => {
                setMounted(false);
            }, 320);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Close on Escape key and handle body scroll lock
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeSidebar();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (isOpen) document.body.style.overflow = '';
        };
    }, [isOpen, closeSidebar]);

    // STRICT LAZY LOADING: If closed and unmounted, do NOT render in DOM
    if (!mounted) {
        return null;
    }

    // Determine banner URL (desktop / mobile responsive fallback)
    const desktopBanner = data?.banner?.desktop?.sourceUrl;
    const mobileBanner = data?.banner?.mobile?.sourceUrl;
    const bannerUrl = desktopBanner || mobileBanner;
    const bannerAlt =
        data?.banner?.desktop?.altText || data?.banner?.mobile?.altText || 'اپلیکیشن تترچند';

    const appLogoUrl = data?.appLogo?.sourceUrl;

    // Partition Store Links: Android (Bazaar, Myket, Play Store) vs Web App
    const allLinks = data?.appLinks || [];
    const androidLinks = allLinks.filter((item) => {
        const title = (item.image?.title || item.link?.title || '').toLowerCase();
        const url = (item.link?.url || '').toLowerCase();
        return !title.includes('web') && !url.includes('app.khanesarmaye');
    });

    const webAppLink = allLinks.find((item) => {
        const title = (item.image?.title || item.link?.title || '').toLowerCase();
        const url = (item.link?.url || '').toLowerCase();
        return title.includes('web') || url.includes('app.khanesarmaye');
    });

    interface AndroidItem {
        url: string;
        target?: string;
        title: string;
        imgUrl?: string;
        alt?: string;
        key?: string;
        icon?: React.ReactNode;
        bgColor?: string;
    }

    // Fallbacks if ACF data is empty
    const androidList: AndroidItem[] =
        androidLinks.length > 0
            ? androidLinks.map((item) => ({
                  url: item.link?.url || '#',
                  target: item.link?.target || '_blank',
                  title: item.link?.title || item.image?.title || 'دریافت اپلیکیشن',
                  imgUrl: item.image?.sourceUrl,
                  alt: item.image?.altText || 'دانلود نسخه اندروید',
                  key: item.image?.title || item.link?.url || 'android-link',
              }))
            : [
                  {
                      url: 'https://cafebazaar.ir/app/com.khanesarmaye.app.tether',
                      target: '_blank',
                      title: 'کافه‌بازار',
                      icon: <BazarSvg className='size-5' />,
                      bgColor: 'bg-[#0ea960]',
                      key: 'bazar',
                  },
                  {
                      url: 'https://myket.ir/app/com.khanesarmaye.app.tether',
                      target: '_blank',
                      title: 'مایکت',
                      icon: <MyKetSvg className='size-5' />,
                      bgColor: 'bg-[#3394ff]',
                      key: 'myket',
                  },
                  {
                      url: 'https://play.google.com/store/apps/details?id=com.khanesarmaye.app.tether&pcampaignid=web_share',
                      target: '_blank',
                      title: 'Google Play',
                      icon: null,
                      bgColor: 'bg-black border border-white/10',
                      key: 'google-play',
                  },
              ];

    const webAppUrl = webAppLink?.link?.url || 'https://app.khanesarmaye.com/home';

    // Component: Android 3-item row + Separator + Web App card (or iOS-tailored view)
    const renderDownloadSection = () => {
        if (isIOS) {
            return (
                <div className='flex flex-col gap-3 rounded-2xl border border-emerald-500/25 dark:border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.08] via-emerald-500/[0.02] to-transparent p-3.5 sm:p-4 shadow-sm'>
                    {/* Header: Globe Icon + Title + Badge */}
                    <div className='flex items-center gap-3'>
                        <div className='size-9 rounded-xl bg-emerald-500/15 dark:bg-emerald-500/25 border border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0 shadow-xs'>
                            <Globe className='size-5' />
                        </div>
                        <div className='flex flex-col text-right'>
                            <div className='flex items-center gap-2'>
                                <span className='font-bold text-sm text-zinc-900 dark:text-white leading-tight'>
                                    وب‌اپلیکیشن تترچند (PWA)
                                </span>
                            </div>
                            <span className='text-xs text-zinc-500 dark:text-zinc-400 mt-1 leading-normal'>
                                دسترسی مستقیم و بدون نیاز به اپ‌استور
                            </span>
                        </div>
                    </div>

                    {/* Action Button: Full-width, prominent, luxury gradient */}
                    <a
                        href={webAppUrl}
                        target='_blank'
                        rel='nofollow noopener noreferrer'
                        title='ورود به وب‌اپلیکیشن تترچند'
                        className='group relative flex items-center justify-center gap-2 w-full h-11 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-bold text-sm shadow-md shadow-emerald-600/20 hover:shadow-emerald-500/30 transition-all duration-200 active:scale-[0.98]'
                    >
                        <span>ورود به وب‌اپلیکیشن</span>
                        <ExternalLink className='size-4 group-hover:scale-110 transition-transform' />
                    </a>
                </div>
            );
        }

        return (
            <div className='flex flex-col gap-3'>
                {/* Section Header */}
                <div className='flex items-center gap-1.5 text-xs font-bold text-zinc-800 dark:text-zinc-200'>
                    <Download2LineIcon className='size-4 text-emerald-500' />
                    <span>دانلود مستقیم نسخه اندروید</span>
                </div>

                {/* 3 Android Badges in ONE LINE */}
                <div className='grid grid-cols-3 gap-2 sm:gap-2.5'>
                    {androidList.map((item) => (
                        <a
                            key={item.key}
                            href={item.url}
                            target={item.target || '_blank'}
                            rel='nofollow noopener noreferrer'
                            title={item.title}
                            className='group relative flex items-center justify-center w-full h-10 sm:h-11 rounded-xl overflow-hidden border border-gray-200/90 dark:border-white/20 bg-black hover:border-emerald-500/60 shadow-xs hover:shadow-md hover:shadow-emerald-500/10 transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98]'
                        >
                            {item.imgUrl ? (
                                <Image
                                    src={item.imgUrl}
                                    alt={item.alt || item.title}
                                    fill
                                    sizes='(max-width: 640px) 30vw, 160px'
                                    className='object-contain p-1'
                                />
                            ) : (
                                <div
                                    className={`w-full h-full flex items-center justify-center gap-1.5 text-white font-semibold text-xs ${
                                        item.bgColor || 'bg-black'
                                    }`}
                                >
                                    {item.icon}
                                    <span>{item.title}</span>
                                </div>
                            )}
                        </a>
                    ))}
                </div>

                {/* Subtle Sleek Separator */}
                <div className='relative flex items-center justify-center mt-3 mb-0.5'>
                    <div className='w-full border-t border-gray-200/70 dark:border-white/10' />
                    <span className='absolute px-2.5 text-[10.5px] font-medium text-zinc-400 dark:text-zinc-500 bg-white dark:bg-[#070c1e]'>
                        نسخه وب و iOS
                    </span>
                </div>

                {/* Clean, Attractive Web App Entry */}
                <a
                    href={webAppUrl}
                    target='_blank'
                    rel='nofollow noopener noreferrer'
                    title='ورود به وب‌اپلیکیشن تترچند'
                    className='group relative flex items-center justify-between p-2.5 sm:p-3.5 rounded-2xl border border-emerald-500/20 dark:border-emerald-500/30 bg-emerald-500/[0.04] dark:bg-emerald-500/[0.06] hover:bg-emerald-50/10 dark:hover:bg-emerald-500/15 hover:border-emerald-500/50 shadow-xs hover:shadow-md hover:shadow-emerald-500/10 transition-all duration-200 cursor-pointer overflow-hidden'
                >
                    <div className='flex items-center gap-2.5 sm:gap-3 min-w-0'>
                        <div className='size-9 sm:size-10 rounded-xl bg-emerald-500/15 dark:bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0 group-hover:scale-105 transition-transform'>
                            <Globe className='size-4 sm:size-5' />
                        </div>
                        <div className='flex flex-col text-right min-w-0'>
                            <div className='flex items-center gap-1.5'>
                                <span className='font-bold text-xs sm:text-sm text-zinc-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors'>
                                    وب‌اپلیکیشن (PWA)
                                </span>
                                <span className='text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 shrink-0'>
                                    iOS و مرورگر
                                </span>
                            </div>
                            <span className='text-[10.5px] sm:text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5'>
                                دسترسی مستقیم و بدون نیاز به نصب
                            </span>
                        </div>
                    </div>

                    <div className='flex items-center gap-1 sm:gap-1.5 px-3 py-1.5 sm:py-2 rounded-xl bg-emerald-600 group-hover:bg-emerald-500 text-white font-bold text-xs shrink-0 shadow-xs transition-transform group-hover:scale-105 mr-1 sm:mr-0'>
                        <span>ورود</span>
                        <ExternalLink className='size-3 sm:size-3.5' />
                    </div>
                </a>
            </div>
        );
    };

    return (
        <div className={`fixed inset-0 z-[10000000] pointer-events-none`} aria-hidden={!isOpen}>
            {/* Backdrop with smooth blur */}
            <div
                onClick={closeSidebar}
                className={`fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 pointer-events-auto ${
                    isAnimated ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                aria-label='بستن مودال'
            />

            {/* ======================================================== */}
            {/* 1. DESKTOP MODAL (md and above): Centered Luxury Dialog */}
            {/* ======================================================== */}
            <div className='hidden md:flex fixed inset-0 items-center justify-center p-6 pointer-events-none'>
                <div
                    dir='rtl'
                    className={`relative w-full max-w-3xl bg-white/95 dark:bg-[#070c1e]/95 backdrop-blur-xl text-zinc-900 dark:text-white rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] border border-gray-200/90 dark:border-white/10 flex flex-col pointer-events-auto transition-all duration-300 ease-out overflow-hidden ${
                        isAnimated
                            ? 'scale-100 opacity-100'
                            : 'scale-95 opacity-0 pointer-events-none'
                    }`}
                    role='dialog'
                    aria-modal='true'
                    aria-label='اپلیکیشن تترچند'
                >
                    {/* Desktop Header */}
                    <div className='flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.02]'>
                        <div className='flex items-center gap-3'>
                            <div className='relative flex items-center justify-center size-11 rounded-2xl overflow-hidden border border-progress/30 bg-emerald-500/10 dark:bg-emerald-500/20 shadow-sm shrink-0'>
                                {appLogoUrl ? (
                                    <Image
                                        src={appLogoUrl}
                                        alt={data?.appLogo?.altText || 'لوگوی تترچند'}
                                        fill
                                        sizes='44px'
                                        className='object-cover'
                                    />
                                ) : (
                                    <TetherCoin3D className='size-8' />
                                )}
                            </div>
                            <div className='flex flex-col'>
                                <div className='flex items-center gap-2'>
                                    <span className='font-black text-base text-zinc-900 dark:text-white'>
                                        اپلیکیشن تترچند
                                    </span>
                                    <span className='px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25'>
                                        نسخه رسمی
                                    </span>
                                </div>
                                <span className='text-xs text-zinc-500 dark:text-zinc-400 font-normal mt-0.5'>
                                    رصد هوشمند قیمت ارزهای دیجیتال، فیات و صرافی‌ها
                                </span>
                            </div>
                        </div>

                        <div className='flex items-center gap-2'>
                            <button
                                onClick={closeSidebar}
                                aria-label='بستن مودال'
                                className='group flex items-center gap-1.5 p-2 rounded-full text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors text-xs cursor-pointer'
                            >
                                <CloseLineIcon size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Desktop Body (2 Columns Top + Full-width Content Below) */}
                    <div className='p-5 sm:p-6 grid grid-cols-12 gap-4 items-start'>
                        {isLoading && !data ? (
                            <div className='col-span-12 grid grid-cols-12 gap-4 animate-pulse'>
                                <div className='col-span-6 h-[146px] bg-gray-200 dark:bg-white/10 rounded-2xl' />
                                <div className='col-span-6 space-y-3'>
                                    <div className='h-5 bg-gray-200 dark:bg-white/10 rounded w-1/2' />
                                    <div className='grid grid-cols-3 gap-2.5'>
                                        <div className='h-10 bg-gray-200 dark:bg-white/10 rounded-xl' />
                                        <div className='h-10 bg-gray-200 dark:bg-white/10 rounded-xl' />
                                        <div className='h-10 bg-gray-200 dark:bg-white/10 rounded-xl' />
                                    </div>
                                    <div className='h-12 bg-gray-200 dark:bg-white/10 rounded-2xl' />
                                </div>
                                <div className='col-span-12 space-y-2 pt-2 border-t border-gray-100 dark:border-white/10'>
                                    <div className='h-4 bg-gray-200 dark:bg-white/10 rounded w-full' />
                                    <div className='h-4 bg-gray-200 dark:bg-white/10 rounded w-3/4' />
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Right Column: Banner */}
                                <div className='col-span-6 flex flex-col'>
                                    {bannerUrl && (
                                        <div className='relative w-full aspect-[16/9] overflow-hidden rounded-2xl border border-gray-200/80 dark:border-white/10 shadow-sm bg-gray-100 dark:bg-white/5'>
                                            <img
                                                src={desktopBanner || bannerUrl}
                                                alt={bannerAlt}
                                                className='w-full h-full object-cover'
                                                loading='eager'
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Left Column: Android Store Badges (1 Row) + Separator + Web App */}
                                <div className='col-span-6 flex flex-col'>
                                    {renderDownloadSection()}
                                </div>

                                {/* Full-width Dynamic WordPress Content / Description */}
                                {data?.content ? (
                                    <div className='col-span-12 pt-2'>
                                        <div
                                            className='text-xs leading-[1.7] text-zinc-500 dark:text-zinc-400 font-normal prose prose-sm dark:prose-invert max-w-none text-right [&_p]:text-zinc-500 dark:[&_p]:text-zinc-400 [&_p]:text-xs [&_p]:leading-relaxed [&_p]:m-0 [&_ul]:space-y-1.5 [&_ul]:my-1.5 [&_li]:relative [&_li]:pr-4 [&_li]:list-none [&_li]:text-zinc-500 dark:[&_li]:text-zinc-400 [&_li::before]:content-[""] [&_li::before]:absolute [&_li::before]:right-0 [&_li::before]:top-[0.55em] [&_li::before]:size-1.5 [&_li::before]:rounded-full [&_li::before]:bg-emerald-500'
                                            dangerouslySetInnerHTML={{
                                                __html: safeHtmlLinks(data.content),
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>

                    {/* Desktop Footer */}
                    <div className='px-6 py-3 border-t border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.02] flex items-center justify-between text-xs text-zinc-400 dark:text-zinc-500'>
                        <span>خانه سرمایه • سامانه هوشمند رصد بازار ارز</span>
                        <span className='flex items-center gap-1 text-[11px]'>
                            <Sparkles className='size-3 text-emerald-500' />
                            <span>تترچند • رصد لحظه‌ای رمزارزها و صرافی‌ها</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* ======================================================== */}
            {/* 2. MOBILE DRAWER (< md): Compact Left Slide Drawer */}
            {/* ======================================================== */}
            <div
                dir='rtl'
                className={`md:hidden fixed inset-y-0 left-0 w-[88vw] max-w-[360px] bg-white dark:bg-[#070c1e] text-zinc-900 dark:text-white shadow-2xl border-r border-gray-200/90 dark:border-white/10 flex flex-col pointer-events-auto transition-transform duration-300 ease-in-out will-change-transform overflow-hidden z-[10000001] ${
                    isAnimated ? 'translate-x-0' : '-translate-x-full pointer-events-none'
                }`}
                role='dialog'
                aria-modal='true'
                aria-label='اپلیکیشن تترچند'
            >
                {/* Mobile Header */}
                <div className='flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/10 bg-white/80 dark:bg-[#070c1e]/80 backdrop-blur-md sticky top-0 z-10'>
                    <div className='flex items-center gap-2.5 min-w-0'>
                        <div className='relative flex items-center justify-center size-9 rounded-xl overflow-hidden border border-progress/50 bg-primary/10 dark:bg-progress/20 shadow-sm shrink-0'>
                            {appLogoUrl ? (
                                <Image
                                    src={appLogoUrl}
                                    alt={data?.appLogo?.altText || 'لوگوی تترچند'}
                                    fill
                                    sizes='36px'
                                    className='object-cover'
                                />
                            ) : (
                                <TetherCoin3D className='size-6' />
                            )}
                        </div>
                        <div className='flex flex-col min-w-0'>
                            <span className='font-bold text-sm text-zinc-900 dark:text-white leading-tight'>
                                اپلیکیشن تترچند
                            </span>
                            <span className='text-[10.5px] text-zinc-500 dark:text-zinc-400 font-normal leading-tight mt-0.5 truncate max-w-[195px]'>
                                رصد هوشمند ارزهای دیجیتال و صرافی‌ها
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={closeSidebar}
                        aria-label='بستن سایدبار'
                        className='p-1.5 rounded-lg text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors shrink-0'
                    >
                        <CloseLineIcon size={22} />
                    </button>
                </div>

                {/* Mobile Scrollable Content */}
                <div className='flex-1 overflow-y-auto px-4 py-3.5 space-y-3.5'>
                    {isLoading && !data ? (
                        <div className='space-y-3.5 animate-pulse'>
                            <div className='w-full aspect-[2/1] max-h-[140px] rounded-2xl bg-gray-200 dark:bg-white/10' />
                            <div className='space-y-2'>
                                <div className='h-4 bg-gray-200 dark:bg-white/10 rounded w-3/4' />
                                <div className='h-4 bg-gray-200 dark:bg-white/10 rounded w-full' />
                            </div>
                            <div className='grid grid-cols-3 gap-2 pt-1'>
                                <div className='h-10 bg-gray-200 dark:bg-white/10 rounded-xl' />
                                <div className='h-10 bg-gray-200 dark:bg-white/10 rounded-xl' />
                                <div className='h-10 bg-gray-200 dark:bg-white/10 rounded-xl' />
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Banner */}
                            {bannerUrl && (
                                <div className='relative w-full aspect-[2/1] overflow-hidden rounded-2xl border border-gray-200/80 dark:border-white/10 shadow-sm bg-gray-100 dark:bg-white/5'>
                                    <img
                                        src={mobileBanner || bannerUrl}
                                        alt={bannerAlt}
                                        className='w-full h-full object-cover'
                                        loading='eager'
                                    />
                                </div>
                            )}

                            {/* Content (with custom emerald bullets) */}
                            {data?.content ? (
                                <div
                                    className='text-[11.5px] sm:text-xs leading-relaxed text-zinc-500 dark:text-zinc-400 font-normal prose prose-sm dark:prose-invert max-w-none border-b border-gray-100 dark:border-white/10 pb-2.5 [&_p]:text-zinc-500 dark:[&_p]:text-zinc-400 [&_p]:text-[11.5px] sm:[&_p]:text-xs [&_p]:leading-relaxed [&_p]:m-0 [&_p]:font-normal [&_ul]:space-y-1.5 [&_ul]:my-2 [&_li]:relative [&_li]:pr-4 [&_li]:list-none [&_li]:text-zinc-500 dark:[&_li]:text-zinc-400 [&_li::before]:content-[""] [&_li::before]:absolute [&_li::before]:right-0 [&_li::before]:top-[0.55em] [&_li::before]:size-1.5 [&_li::before]:rounded-full [&_li::before]:bg-emerald-500'
                                    dangerouslySetInnerHTML={{
                                        __html: safeHtmlLinks(data.content),
                                    }}
                                />
                            ) : null}

                            {/* Download Section (3 Android Badges + Separator + Web App) */}
                            {renderDownloadSection()}
                        </>
                    )}
                </div>

                {/* Mobile Footer */}
                <div className='px-4 py-2.5 border-t border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.02] text-center shrink-0'>
                    <span className='text-[10.5px] text-zinc-400 dark:text-zinc-500'>
                        خانه سرمایه • سامانه هوشمند رصد بازار ارز
                    </span>
                </div>
            </div>
        </div>
    );
}
